import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Send, Save, Eye, FileText, Loader2, Trash2, Check } from 'lucide-react';

const VAR_HINTS = {
  contract: [
    { key: '{{first_name}}', desc: 'Candidate first name' },
    { key: '{{last_name}}', desc: 'Candidate last name' },
    { key: '{{role}}', desc: 'Position title' },
    { key: '{{company}}', desc: 'Company name' },
    { key: '{{recruiter}}', desc: 'Your name' },
    { key: '{{today}}', desc: "Today's date" },
  ],
  rejection: [
    { key: '{{first_name}}', desc: 'Candidate first name' },
    { key: '{{last_name}}', desc: 'Candidate last name' },
    { key: '{{role}}', desc: 'Position title' },
    { key: '{{company}}', desc: 'Company name' },
    { key: '{{recruiter}}', desc: 'Your name' },
    { key: '{{match_score}}', desc: 'Match score %' },
    { key: '{{today}}', desc: "Today's date" },
  ],
};

const EmailTemplateModal = ({ open, onOpenChange, type, application, recruiterId, organizationId, onSent }) => {
  const isContract = type === 'contract';
  const title = isContract ? 'Send contract email' : 'Send rejection email';

  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const [previewMode, setPreviewMode] = useState(false);
  const [preview, setPreview] = useState(null);

  const [saveAsName, setSaveAsName] = useState('');
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);

  const candidateEmail = application?.users?.email || application?.user?.email || application?.prospect?.email || application?.email || '';
  const candidateName = `${application?.users?.first_name || application?.prospect?.firstName || ''} ${application?.users?.last_name || application?.prospect?.lastName || ''}`.trim();

  // Load templates whenever modal opens
  useEffect(() => {
    if (!open || !organizationId) return;
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/email-templates`, {
          params: { orgId: organizationId, type },
        });
        if (cancel) return;
        const list = r.data?.data || [];
        setTemplates(list);
        // pick the first (built-in Default) on open
        if (list[0]) {
          setSelectedId(list[0].id);
          setSubject(list[0].subject);
          setBody(list[0].body);
        }
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, organizationId, type]);

  // When user picks a template from the dropdown
  const chooseTemplate = (id) => {
    setSelectedId(id);
    const t = templates.find(x => x.id === id);
    if (t) {
      setSubject(t.subject);
      setBody(t.body);
    }
  };

  const insertVariable = (key) => {
    setBody(b => `${b}${b && !b.endsWith('\n') ? ' ' : ''}${key}`);
  };

  const handlePreview = async () => {
    try {
      setLoading(true);
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/email-templates/render`, {
        template_subject: subject,
        template_body: body,
        application_id: application?.id,
        recruiter_id: recruiterId,
      });
      if (r.data?.success) {
        setPreview(r.data.data);
        setPreviewMode(true);
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to render preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      setSending(true);
      setError(null);
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/email-templates/send`, {
        type,
        template_subject: subject,
        template_body: body,
        application_id: application?.id,
        recruiter_id: recruiterId,
      });
      if (r.data?.success) {
        onSent?.({ type, subject: r.data.data?.subject });
        onOpenChange(false);
      } else {
        setError(r.data?.message || 'Send failed');
      }
    } catch (e) {
      const backendMsg = e?.response?.data?.message;
      setError(backendMsg ? `Send failed — ${backendMsg}` : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!saveAsName.trim()) return;
    try {
      setSavingTpl(true);
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/email-templates`, {
        organization_id: organizationId,
        type,
        name: saveAsName.trim(),
        subject,
        body,
        created_by: recruiterId,
      });
      if (r.data?.success) {
        const saved = r.data.data;
        setTemplates(prev => [...prev, saved]);
        setSelectedId(saved.id);
        setSaveAsName('');
        setShowSaveAs(false);
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save template');
    } finally {
      setSavingTpl(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedId || selectedId.startsWith('__builtin_')) return;
    if (!window.confirm('Delete this template? This affects everyone in your company.')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/email-templates/${selectedId}`, {
        params: { orgId: organizationId },
      });
      setTemplates(prev => prev.filter(t => t.id !== selectedId));
      const fallback = templates.find(t => t.id !== selectedId);
      if (fallback) chooseTemplate(fallback.id);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to delete');
    }
  };

  const selected = useMemo(() => templates.find(t => t.id === selectedId), [templates, selectedId]);
  const isBuiltinSelected = selectedId?.startsWith('__builtin_');
  const isDirty = selected && (subject !== selected.subject || body !== selected.body);
  const variables = VAR_HINTS[type] || VAR_HINTS.contract;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`email-modal-${type}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className={`w-4 h-4 ${isContract ? 'text-green-600' : 'text-gray-500'}`} />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Recipient */}
          <div className="flex items-center justify-between text-sm bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
            <div>
              <span className="text-gray-500">To: </span>
              <span className="font-medium text-gray-800">{candidateName || 'Candidate'}</span>
              {candidateEmail && <span className="text-gray-500"> · {candidateEmail}</span>}
            </div>
          </div>

          {!previewMode ? (
            <>
              {/* Template picker */}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs text-gray-500 mb-1 block">Template</Label>
                  <Select value={selectedId} onValueChange={chooseTemplate} disabled={loading}>
                    <SelectTrigger data-testid="email-template-select">
                      <SelectValue placeholder="Choose a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}{t.is_default ? ' (built-in)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {!isBuiltinSelected && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteTemplate}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="email-delete-template"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>

              {/* Subject */}
              <div>
                <Label htmlFor="em-subject" className="text-xs text-gray-500 mb-1 block">Subject</Label>
                <Input
                  id="em-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="email-subject"
                />
              </div>

              {/* Body */}
              <div>
                <Label htmlFor="em-body" className="text-xs text-gray-500 mb-1 block">Message</Label>
                <textarea
                  id="em-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={12}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="email-body"
                />
              </div>

              {/* Variables */}
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Click to insert variable:</p>
                <div className="flex flex-wrap gap-1.5">
                  {variables.map(v => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => insertVariable(v.key)}
                      title={v.desc}
                      className="text-[11px] font-mono bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-700 px-1.5 py-0.5 rounded border border-gray-200"
                      data-testid={`email-var-${v.key.replace(/[{}]/g, '')}`}
                    >
                      {v.key}
                    </button>
                  ))}
                </div>
              </div>

              {/* Save as template */}
              {!showSaveAs ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSaveAs(true)}
                  className="w-fit"
                  data-testid="email-save-as-btn"
                >
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                  Save as company template
                </Button>
              ) : (
                <div className="bg-blue-50/40 border border-blue-100 rounded-md p-3 space-y-2">
                  <p className="text-xs text-blue-700">All recruiters in your company will see this template.</p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Template name (e.g. Standard offer)"
                      value={saveAsName}
                      onChange={(e) => setSaveAsName(e.target.value)}
                      data-testid="email-save-name"
                    />
                    <Button onClick={handleSaveAsTemplate} disabled={!saveAsName.trim() || savingTpl} className="bg-blue-600 hover:bg-blue-700" data-testid="email-save-confirm">
                      {savingTpl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button variant="outline" onClick={() => { setShowSaveAs(false); setSaveAsName(''); }}>Cancel</Button>
                  </div>
                </div>
              )}

              {isDirty && !isBuiltinSelected && (
                <p className="text-[11px] text-amber-600">⚠️ You&apos;ve edited this template. Save again to share the changes with your team.</p>
              )}
            </>
          ) : (
            /* PREVIEW MODE */
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="email-preview">
              <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                <p className="text-[10px] uppercase tracking-wide text-gray-400">Subject</p>
                <p className="text-sm font-medium text-gray-900">{preview?.subject}</p>
              </div>
              <div className="px-4 py-3 bg-white whitespace-pre-wrap text-sm text-gray-800 leading-relaxed max-h-80 overflow-y-auto">
                {preview?.body}
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <DialogFooter className="gap-2 mt-2">
          {previewMode ? (
            <>
              <Button variant="outline" onClick={() => setPreviewMode(false)} data-testid="email-edit-back">Edit</Button>
              <Button onClick={handleSend} disabled={sending} className={isContract ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} data-testid="email-send-confirm">
                <Send className="w-3.5 h-3.5 mr-1.5" />
                {sending ? 'Sending…' : 'Send email'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="email-cancel">Cancel</Button>
              <Button variant="outline" onClick={handlePreview} disabled={!subject.trim() || !body.trim() || loading} data-testid="email-preview-btn">
                <Eye className="w-3.5 h-3.5 mr-1.5" />
                Preview
              </Button>
              <Button onClick={handleSend} disabled={!subject.trim() || !body.trim() || sending} className={isContract ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} data-testid="email-send-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" />
                {sending ? 'Sending…' : 'Send email'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailTemplateModal;
